import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    MAX_TRANSACTION_REFERENCES,
    TransactionReference,
} from "@common/schemas/transactionReference.schema";
import {
    DraftRefsState,
    DraftRefsTarget,
    useDraftRefsByTarget,
    useTargetDraftRefs,
} from "./useDraftRefsByTarget";

export interface TransactionRefContextValue extends DraftRefsState<TransactionReference> {
    focusedTransactionId: number | null;
    setFocusedTransactionId: React.Dispatch<
        React.SetStateAction<number | null>
    >;
}

function buildDraftRefDedupKey(ref: TransactionReference): string {
    return `${ref.taskId}::${ref.transactionId}`;
}

const TransactionRefContext = createContext<TransactionRefContextValue | null>(
    null,
);

interface TransactionRefProviderProps {
    taskId: string;
    children: React.ReactNode;
}

export const TransactionRefProvider: React.FC<TransactionRefProviderProps> = ({
    taskId,
    children,
}) => {
    const draftRefsState = useDraftRefsByTarget({
        taskId,
        maxRefs: MAX_TRANSACTION_REFERENCES,
        getDedupKey: buildDraftRefDedupKey,
    });
    const [focusedTransactionId, setFocusedTransactionId] = useState<
        number | null
    >(null);

    useEffect(() => {
        setFocusedTransactionId(null);
    }, [taskId]);

    const value = useMemo(
        () => ({
            ...draftRefsState,
            focusedTransactionId,
            setFocusedTransactionId,
        }),
        [draftRefsState, focusedTransactionId],
    );

    return (
        <TransactionRefContext.Provider value={value}>
            {children}
        </TransactionRefContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useTransactionRefContextMaybe(): TransactionRefContextValue | null {
    return useContext(TransactionRefContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDraftTransactionRefsForTarget(target: DraftRefsTarget) {
    return useTargetDraftRefs({
        draftRefsState: useTransactionRefContextMaybe(),
        target,
    });
}
